import React, {useEffect} from 'react';
import {AppProps} from 'next/app';
import {CookieStorage} from 'cookie-storage';
import Head from 'next/head';
import Router from 'next/router';
import {ThemeProvider} from '@mui/material/styles';
import {observer} from 'mobx-react';
import {PageProps, useStore, withStore} from '../components/StoreProvider';
import {GlobalError} from '../components/global-error';
import {analyticsEnabled, hotjarId, hotjarSv, tagManagerId} from '../config';
import {clearAnalyticsDeclined, rememberAnalyticsDeclined} from '../helpers/anonymousAnalytics';
import {checkOpenedChat} from '../helpers/checkOpenedChat';
import applyOverscrollClass from '../helpers/mobileFbBrowserAdjustment';
import {
    ConsentCategory,
    ConsentCategoryName,
    denyAdvertisingSignalsForGpc,
    hasConsent,
    onConsentChange,
    revokeHubSpotConsent,
    syncHubSpotConsent,
} from '../helpers/consent';
import useCookieBannerOffset from '../helpers/useCookieBannerOffset';
import {clearTrackingCookies} from '../helpers/clearTrackingCookies';
import {theme} from '../helpers/theme';
import RouterHoc from '../layouts/RouterHoc/RouterHoc';
import {actions, categories, pushEvent} from '../helpers/analyticsEvents';
import {serialize} from '../store/sync/serialization';
import '../styles/globals.scss';
import '../styles/termly-cmp.css';
import {useDetectAdBlock} from 'adblock-detect-react';

declare global {
    interface Window {
        dataLayer: unknown[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        opera: any;
        ga: (action: string, arg1: string, arg2: string) => void;
        _hsq: {push: (args: Array<string>) => void};
        HubSpotConversations: {
            widget: {
                load: () => void;
                status: () => {loaded: boolean};
            };
        };
        hsConversationsOnReady: Array<() => void>;
    }
}

function useAdBlockDetector() {
    const store = useStore();
    const adBlockDetected = useDetectAdBlock();

    useEffect(() => {
        if (adBlockDetected)
            store.logStore.logAction({
                action: 'detected-adblock',
                data: serialize(store.userData),
            });
    }, [adBlockDetected, store]);
}

/**
 * Loads the third-party tags each category covers, and never before that category is granted.
 *
 * Everything here initialises at most once: `onConsentChange` fires immediately and again on
 * every change, so a visitor who accepts after the page has loaded still gets the tags without
 * a reload. Withdrawal is the other direction and cannot be undone in place — scripts already
 * executed keep running — so that path reloads instead.
 */
function useConsentedAnalytics() {
    const store = useStore();

    useEffect(() => {
        let tagManagerStarted = false;
        let pixelStarted = false;
        let consentRecorded = false;
        // Registered from inside an async import, so it does not exist yet when this effect
        // returns — the cleanup has to reach it through a variable rather than a closure.
        let pixelPageView: (() => void) | undefined;
        // Only a downgrade needs the reload; the first grant is handled in place above.
        let grantedSoFar = {analytics: false, advertising: false};

        const unsubscribe = onConsentChange(({initial}) => {
            const analytics = hasConsent(ConsentCategory.analytics);
            const advertising = hasConsent(ConsentCategory.advertising);

            // Relaying the decision is not analytics, so it is deliberately outside the
            // analyticsEnabled check below: HubSpot must be told what the visitor chose in every
            // environment, or it withholds its cookies for ever and the grant path cannot be
            // tested anywhere but production.
            //
            // HubSpot gates its own cookies from this, so its script is never blocked and
            // lead-form capture keeps working for visitors who have not consented.
            syncHubSpotConsent();

            // Termly re-grants ad_storage from its own state on each change, so this has to
            // follow it rather than run once.
            denyAdvertisingSignalsForGpc();

            // Only the third-party tags are production-only, so analyticsEnabled guards them
            // individually rather than the whole handler: dev and staging traffic must not reach
            // the live GTM, Hotjar and Pixel accounts, but withdrawing consent still has to
            // revoke it with HubSpot, and declining still has to stop the anonymous events.
            if (analyticsEnabled && analytics && !tagManagerStarted) {
                tagManagerStarted = true;
                Promise.all([import('react-gtm-module'), import('react-hotjar')]).then(
                    ([TagManagerModule, hotjarModule]) => {
                        TagManagerModule.default.initialize({gtmId: tagManagerId});
                        hotjarModule.hotjar.initialize(hotjarId, hotjarSv);
                        pushEvent(categories.initialLanding, actions.pageView);
                    },
                );
            }

            // In production the Pixel is served from GTM instead (NEXT_PUBLIC_DISABLE_FACEBOOK_PIXEL),
            // so this branch is compiled out there and only runs in dev and staging.
            if (
                analyticsEnabled &&
                advertising &&
                !pixelStarted &&
                process.env.NEXT_PUBLIC_DISABLE_FACEBOOK_PIXEL !== 'true'
            ) {
                pixelStarted = true;
                import('react-facebook-pixel')
                    .then((x) => x.default)
                    .then((ReactPixel) => {
                        ReactPixel.init(process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID || '526430414837727');
                        ReactPixel.pageView();

                        pixelPageView = () => {
                            ReactPixel.pageView();
                        };
                        Router.events.on('routeChangeComplete', pixelPageView);
                    });
            }

            if (analytics && !consentRecorded) {
                consentRecorded = true;
                // Close the anonymous session first, so it reads as "ended in consent" rather
                // than as an abandonment, then open the identified funnel. Neither event
                // references the other's identifier.
                store.logStore.logAnonymousSessionConsented();
                store.logStore.logConsentGranted();
                // Joins the two together on one profile. Mixpanel's merge is retroactive, so it
                // does not matter whether it lands before or after the pair above.
                store.logStore.mergeAnonymousSession();
            }

            // Only an actual decision counts. On the first run analytics is false simply
            // because nobody has chosen yet, and persisting that as a decline silenced the
            // anonymous funnel for the whole visit and every visit after it.
            if (analytics) clearAnalyticsDeclined();
            else if (!initial) rememberAnalyticsDeclined();

            const withdrawn = [
                grantedSoFar.analytics && !analytics ? ConsentCategory.analytics : null,
                grantedSoFar.advertising && !advertising ? ConsentCategory.advertising : null,
            ].filter(Boolean) as ConsentCategoryName[];
            grantedSoFar = {analytics, advertising};

            if (withdrawn.length) {
                // Before the reload, so the identifiers do not outlive the decision. Scoped to
                // the categories actually withdrawn — dropping a kept category's cookies would
                // reset that measurement for no reason.
                clearTrackingCookies(withdrawn);
                revokeHubSpotConsent();
                window.location.reload();
            }
        });

        return () => {
            unsubscribe();
            // A no-op when the Pixel never started, or its import never resolved.
            if (pixelPageView) Router.events.off('routeChangeComplete', pixelPageView);
        };
    }, [store]);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function App(props: AppProps<PageProps>) {
    const mobxStore = useStore();
    const cookieStorage = new CookieStorage();
    useAdBlockDetector();
    useCookieBannerOffset();
    useConsentedAnalytics();

    useEffect(() => {
        if (!cookieStorage) return;

        // Named so the cleanup below can remove it; an inline handler cannot be unregistered.
        const trackRouteChange = () => pushEvent(categories.routeChangeComplete, actions.routeChange);

        checkOpenedChat(cookieStorage);
        import('../api/injector').then(({utmInjector}) => {
            utmInjector(mobxStore);
        });
        const queryParams = new URLSearchParams(window.location.search);
        const prefillDate = queryParams.get('prefill[dateOfBirth]');
        if (prefillDate !== null) {
            const date = new Date(prefillDate);
            mobxStore.userData.phase1.updateField('dateOfBirth')(
                `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}/${date.getFullYear()}`,
            );
            const prefillGender = queryParams.get('prefill[gender]');
            if (prefillGender !== null)
                mobxStore.userData.phase1.updateField('gender')(prefillGender as 'male' | 'female' | '');
            const prefillZip = queryParams.get('prefill[zip]');
            if (prefillZip !== null) mobxStore.userData.phase1.updateField('zipCode')(prefillZip);
            const prefillMaritalStatus = queryParams.get('prefill[maritialStatus]');
            if (prefillMaritalStatus !== null)
                mobxStore.userData.phase1.updateField('maritalStatus')(
                    (prefillMaritalStatus?.charAt(0)?.toUpperCase() + prefillMaritalStatus?.slice(1)) as
                        | 'Single'
                        | 'Married'
                        | 'Divorced'
                        | 'Separated'
                        | 'Widowed'
                        | '',
                );
            const prefillNKids = queryParams.get('prefill[numberOfKids]');
            if (prefillNKids !== null) mobxStore.userData.phase1.updateField('numberOfChildren')(prefillNKids);
            const prefillHouseholdIncome = queryParams.get('prefill[annualHouseholdIncomeNumber]');
            if (prefillHouseholdIncome) {
                mobxStore.userData.phase1.updateField('annualHouseholdIncomeText')(prefillHouseholdIncome);
                mobxStore.userData.phase1.updateField('annualHouseholdIncomeNumeric')(Number(prefillHouseholdIncome));
                mobxStore.userData.phase1.updateField('annualIndividualIncome')(prefillHouseholdIncome);
            }
            const prefillTotalDebt = queryParams.get('prefill[totalDebt]');
            if (prefillTotalDebt !== null)
                mobxStore.userData.phase1.updateField('estimatedDebtTotal')(prefillTotalDebt);
            const prefillTotalRetirementSavings = queryParams.get('prefill[totalRetirementSavings]');
            if (prefillTotalRetirementSavings !== null)
                mobxStore.userData.phase1.updateField('estimatedSavingTotal')(Number(prefillTotalRetirementSavings));
            const prefillCalcKeys = [
                'dateOfBirth',
                'gender',
                'zip',
                'maritialStatus',
                'numberOfKids',
                'annualHouseholdIncomeNumber',
                'totalDebt',
                'totalRetirementSavings',
                'preferredInvestmentRisk',
                'stateId',
                'totalLiquidAssets',
                'yearUntilMoneyNeeded',
                'preferredMonthlySpend',
                'recomendedMonthlySpend',
                'existingLifeInsurance',
            ];
            for (let i = 0; i < prefillCalcKeys.length; i++) {
                if (queryParams.has(`prefill[${prefillCalcKeys[i]}]`)) {
                    queryParams.delete(`prefill[${prefillCalcKeys[i]}]`);
                }
            }
            const newSearch = queryParams.toString();
            const newUrl = `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`;
            window.history.replaceState({}, '', newUrl);
        }
        mobxStore.logStore.logLead('landed');
        mobxStore.logStore.updateBrowserInfo();
        mobxStore.userData.initAutoDefaultPages();

        if (process.env.NEXT_PUBLIC_DISABLE_FACEBOOK_PIXEL === 'true') {
            // we load facebook pixel from gtm instead in order to make it easier
            // for us to disable route change triggers as needed
            // in order to prevent duplicate events being sent back to facebook
            Router.events.on('routeChangeComplete', trackRouteChange);
        }
        mobxStore.userData.phase1.updateField('investmentRisk')('moderate');

        return () => {
            // A no-op when the branch above did not run. cookieStorage is rebuilt on every
            // render, so this effect re-runs on every render too: without the removal each one
            // left another listener behind and the event was sent once per render.
            Router.events.off('routeChangeComplete', trackRouteChange);
        };
    }, [cookieStorage]);

    useEffect(() => {
        const updateBrowserInfo = () => {
            mobxStore.logStore.updateBrowserInfo();
        };

        updateBrowserInfo();

        window.addEventListener('resize', updateBrowserInfo);
        window.addEventListener('orientationchange', updateBrowserInfo);

        return () => {
            window.removeEventListener('resize', updateBrowserInfo);
            window.removeEventListener('orientationchange', updateBrowserInfo);
        };
    }, []);

    useEffect(() => {
        const {os, browser} = mobxStore.logStore || {};
        const shouldDisableOverscroll = os === 'iOS' && browser === 'Facebook Mobile';
        applyOverscrollClass(shouldDisableOverscroll);

        return () => {
            applyOverscrollClass(false);
        };
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.get('landingpage') && mobxStore.userData.experiment?.newLandingPage4050) {
            mobxStore.userData.updateField('landingPage')(queryParams.get('landingpage') || '');
        }
    }, []);

    const {Component, pageProps} = props;

    return (
        <>
            <Head>
                <title>Amplify Quotes</title>
            </Head>
            <ThemeProvider theme={theme}>
                <GlobalError>
                    <RouterHoc>
                        <Component {...pageProps} />
                    </RouterHoc>
                </GlobalError>
            </ThemeProvider>
        </>
    );
}

export default withStore(observer(App));
